import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Unlink, Undo2, Redo2 } from "lucide-react";
import { toHtml } from "@/components/RichText";

function ToolButton({ onClick, active, label, children }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={label} aria-label={label}
      data-testid={`rte-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${active ? "bg-brand-navy text-white" : "text-brand-ink hover:bg-brand-navy/10"}`}>
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, testId }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false } })],
    content: toHtml(value),
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
    editorProps: { attributes: { class: "rich-text min-h-[120px] px-3 py-2 focus:outline-none" } },
  });

  useEffect(() => () => editor?.destroy(), [editor]);
  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (e.g. https://... or /contact)", prev);
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const c = () => editor.chain().focus();

  return (
    <div className="rounded-md border border-input bg-white" data-testid={testId}>
      <div className="flex flex-wrap gap-1 border-b p-1">
        <ToolButton label="Bold" active={editor.isActive("bold")} onClick={() => c().toggleBold().run()}><Bold className="h-4 w-4" /></ToolButton>
        <ToolButton label="Italic" active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolButton>
        <ToolButton label="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolButton>
        <ToolButton label="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></ToolButton>
        <ToolButton label="Bullet list" active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()}><List className="h-4 w-4" /></ToolButton>
        <ToolButton label="Numbered list" active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolButton>
        <ToolButton label="Link" active={editor.isActive("link")} onClick={setLink}><Link2 className="h-4 w-4" /></ToolButton>
        <ToolButton label="Remove link" onClick={() => c().unsetLink().run()}><Unlink className="h-4 w-4" /></ToolButton>
        <span className="mx-1 w-px bg-border" />
        <ToolButton label="Undo" onClick={() => c().undo().run()}><Undo2 className="h-4 w-4" /></ToolButton>
        <ToolButton label="Redo" onClick={() => c().redo().run()}><Redo2 className="h-4 w-4" /></ToolButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
